import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

marked.setOptions({ breaks: true, gfm: true })

export default class extends Controller {
  static targets = ["panel", "bubble", "messages", "input", "loading", "sendBtn", "status"]

  // La réponse prend 10 à 30 s (l'IA interroge la base) : on le dit plutôt que
  // de laisser trois points muets.
  static STATUS_STEPS = [
    "Je lis ta question…",
    "Je regarde tes deals et tes contacts…",
    "Je rassemble les chiffres…",
    "Je rédige la réponse…",
    "Encore quelques secondes…"
  ]

  connect() {
    this.conversationId = sessionStorage.getItem("chat_conversation_id")
    this.isOpen = false
    this.isAnimating = false
  }

  toggle() {
    if (this.isAnimating) return
    this.isOpen ? this.closePanel() : this.openPanel()
  }

  openPanel() {
    this.isOpen = true
    this.isAnimating = true
    this.panelTarget.style.display = "flex"
    this.panelTarget.classList.remove("chat-panel-exit")
    this.panelTarget.classList.add("chat-panel-enter")
    this.bubbleTarget.style.display = "none"

    this.panelTarget.addEventListener("animationend", () => {
      this.isAnimating = false
      this.inputTarget.focus()
    }, { once: true })
  }

  closePanel() {
    this.isOpen = false
    this.isAnimating = true
    this.panelTarget.classList.remove("chat-panel-enter")
    this.panelTarget.classList.add("chat-panel-exit")

    this.panelTarget.addEventListener("animationend", () => {
      this.panelTarget.style.display = "none"
      this.bubbleTarget.style.display = "flex"
      this.isAnimating = false
    }, { once: true })
  }

  async send(event) {
    event.preventDefault()
    const message = this.inputTarget.value.trim()
    if (!message) return

    this.appendMessage("user", message)
    this.inputTarget.value = ""
    this.showLoading(true)
    this.sendBtnTarget.disabled = true

    try {
      const response = await fetch("/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          message: message,
          conversation_id: this.conversationId
        })
      })

      if (!response.ok) {
        this.appendMessage("assistant", "Le serveur a rencontre une erreur. Rechargez la page et reessayez.")
        return
      }

      const data = await response.json()
      this.conversationId = data.conversation_id
      sessionStorage.setItem("chat_conversation_id", data.conversation_id)
      this.appendMessage("assistant", data.message.content)
    } catch (error) {
      this.appendMessage("assistant", "Impossible de contacter le serveur. Verifiez votre connexion.")
    } finally {
      this.showLoading(false)
      this.sendBtnTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  appendMessage(role, content) {
    const wrapper = document.createElement("div")
    wrapper.classList.add("flex", "gap-2.5", "chat-msg-enter")

    if (role === "user") {
      wrapper.classList.add("justify-end")
      wrapper.innerHTML = `
        <div class="chat-msg-user">
          <p class="text-sm">${this.escapeHtml(content)}</p>
        </div>
      `
    } else {
      wrapper.innerHTML = `
        <div class="chat-msg-avatar">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
          </svg>
        </div>
        <div class="chat-msg-assistant">
          <div class="text-sm text-text-primary prose prose-sm max-w-none">${marked.parse(content)}</div>
        </div>
      `
    }

    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  showLoading(show) {
    this.loadingTarget.classList.toggle("hidden", !show)
    clearInterval(this.statusTimer)
    if (!this.hasStatusTarget) return
    if (show) {
      let step = 0
      const steps = this.constructor.STATUS_STEPS
      this.statusTarget.textContent = steps[0]
      this.statusTimer = setInterval(() => {
        step = Math.min(step + 1, steps.length - 1)
        this.statusTarget.textContent = steps[step]
      }, 5000)
      this.scrollToBottom()
    } else {
      this.statusTarget.textContent = ""
    }
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
